import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, CheckCircle2, X } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { supabase } from '../lib/supabaseClient';

const auditUrl = 'https://cal.com/synentia-aiagency-tjojr8/free-ai-audit-synentia';

const initialFormData = {
  fullName: '',
  email: '',
  phone: '',
  company: '',
  role: '',
  challenge: '',
  annualRevenue: '',
};

const revenueOptions = [
  'Under $100K',
  '$100K – $500K',
  '$500K – $1M',
  '$1M – $5M',
  '$5M+',
];

export const DemoModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const redirectTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setIsSubmitting(false);
      setIsSuccess(false);
    }
  }, [isOpen]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    const submittedAt = new Date().toISOString();
    const fullName = formData.fullName.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();
    const practiceName = formData.company.trim();
    const role = formData.role.trim();
    const challenge = formData.challenge.trim();
    const revenueRange = formData.annualRevenue;

    if (supabase) {
      try {
        const { error: supabaseError } = await supabase.from('leads').insert({
          full_name: fullName,
          email,
          phone,
          practice_name: practiceName,
          role,
          challenge,
          revenue_range: revenueRange,
          source: 'website',
        });

        if (supabaseError) {
          console.error('Supabase lead insert failed:', supabaseError);
        }
      } catch (error) {
        console.error('Supabase lead insert failed:', error);
      }
    } else {
      console.error('Supabase client not initialized. Check Supabase environment variables.');
    }

    if (supabase) {
      try {
        const { error: invokeError } = await supabase.functions.invoke('send-lead-emails', {
          body: {
            full_name: fullName,
            email,
            phone,
            practice_name: practiceName,
            role,
            challenge,
            revenue_range: revenueRange,
            submitted_at: submittedAt,
          },
        });


        if (invokeError) {
          console.error('Edge function send-lead-emails failed:', invokeError);
        }
      } catch (error) {
        console.error('Edge function send-lead-emails failed:', error);
      }
    } else {
      console.error('Supabase client not initialized for email dispatch.');
    }

    setIsSubmitting(false);
    setIsSuccess(true);

    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
    }

    redirectTimerRef.current = setTimeout(() => {
      window.open(auditUrl, '_blank', 'noopener,noreferrer');
      onClose();
    }, 900);
  };

  if (!isOpen) return null;

  const inputClassName =
    'w-full rounded-lg border border-[--border] bg-[--bg-primary] px-4 py-3 text-sm text-[--text-primary] placeholder:text-[--text-muted] outline-none transition-all focus:border-[--accent] focus:ring-2 focus:ring-[--accent]/25';
  const labelClassName = 'block text-xs font-mono uppercase tracking-[0.14em] text-[--text-secondary]';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-4xl max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)] overflow-hidden rounded-2xl shadow-2xl animate-modal-in">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          aria-label="Close modal"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        <div className="flex h-full flex-col md:flex-row overflow-y-auto">
          <div className="relative hidden md:block bg-[#0a0a0a] text-white p-6 sm:p-8 md:p-12 md:w-1/2 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-t from-transparent to-black/50 z-[1]" />

            <div className="absolute inset-0 flex overflow-hidden opacity-20 z-[2]">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="h-full w-16 bg-gradient-to-b from-transparent via-white/10 to-transparent"
                />
              ))}
            </div>

            <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-[--accent] rounded-full blur-[100px] opacity-40 z-[1]" />
            <div className="absolute bottom-0 left-0 w-32 h-16 bg-white/10 rounded-full blur-xl z-[1]" />

            <div className="relative z-10 h-full flex flex-col justify-between min-h-[220px] sm:min-h-[300px]">
              <div>
                <div className="flex items-center gap-0 mb-8">
                  <span className="text-xl font-semibold tracking-tight">Synentia</span>
                </div>

                <h2 className="text-2xl md:text-3xl font-semibold leading-tight tracking-tight">
                  Transform your business with AI-powered solutions.
                </h2>
              </div>

              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3 text-sm text-white/70">
                  <div className="w-1.5 h-1.5 rounded-full bg-[--accent]" />
                  <span>Free 30-minute consultation</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-white/70">
                  <div className="w-1.5 h-1.5 rounded-full bg-[--accent]" />
                  <span>Custom AI strategy roadmap</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-white/70">
                  <div className="w-1.5 h-1.5 rounded-full bg-[--accent]" />
                  <span>No commitment required</span>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full md:w-1/2 bg-[--bg-secondary]">
            <div className="p-8">
            {isSuccess ? (
              <div className="flex min-h-[520px] flex-col items-center justify-center text-center">
                <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/15">
                  <CheckCircle2 className="h-9 w-9 text-green-500" />
                </div>
                <h3 className="text-2xl font-semibold text-[--text-primary]">
                  Thank you.
                </h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-[--text-secondary]">
                  Your audit request has been received. Opening the calendar now so you can choose a time.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <p className="section-eyebrow mb-3">Lead Capture</p>
                  <h3 className="text-2xl font-semibold tracking-tight text-[--text-primary]">
                    Book a Free Audit
                  </h3>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid gap-x-5 gap-y-5 md:grid-cols-2">
                    <label className="space-y-2">
                      <span className={labelClassName}>Full Name</span>
                      <input
                        required
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        className={inputClassName}
                        placeholder="Jane Smith"
                      />
                    </label>

                    <label className="space-y-2">
                      <span className={labelClassName}>Email</span>
                      <input
                        required
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={inputClassName}
                        placeholder="jane@practice.com"
                      />
                    </label>

                    <div className="space-y-2">
                      <span className={labelClassName}>Phone Number</span>
                      <PhoneInput
                        country="us"
                        value={formData.phone}
                        onChange={(phone) =>
                          setFormData((current) => ({ ...current, phone: `+${phone}` }))
                        }
                        enableSearch
                        searchPlaceholder="Search country..."
                        inputProps={{ required: true, name: 'phone' }}
                        containerClass="phone-input-container"
                        inputClass="phone-input-field"
                        buttonClass="phone-input-button"
                        dropdownClass="phone-input-dropdown"
                      />
                    </div>

                    <label className="space-y-2">
                      <span className={labelClassName}>Company Name</span>
                      <input
                        required
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className={inputClassName}
                        placeholder="Company Name"
                      />
                    </label>

                    <label className="space-y-2 md:col-span-2">
                      <span className={labelClassName}>Role / Title</span>
                      <input
                        required
                        type="text"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        className={inputClassName}
                        placeholder="Operations Director"
                      />
                    </label>

                    <label className="space-y-2 md:col-span-2">
                      <span className={labelClassName}>Biggest operational challenge</span>
                      <textarea
                        required
                        name="challenge"
                        rows={3}
                        value={formData.challenge}
                        onChange={handleChange}
                        className={`${inputClassName} min-h-[100px] resize-none`}
                        placeholder="Tell us what's slowing your team down..."
                      />
                    </label>

                    <label className="space-y-2 md:col-span-2">
                      <span className={labelClassName}>Average Annual Revenue</span>
                      <select
                        required
                        name="annualRevenue"
                        value={formData.annualRevenue}
                        onChange={handleChange}
                        className={inputClassName}
                      >
                        <option value="">Select range</option>
                        {revenueOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[--accent] px-6 py-3.5 text-[11px] font-medium uppercase tracking-[0.18em] text-[--bg-primary] transition-all hover:bg-[--accent-hover] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="h-4 w-4 rounded-full border-2 border-[--bg-primary]/30 border-t-[--bg-primary] animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Book My Free Audit
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoModal;
